import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { GitBranch, Plus, Edit, Trash2, XCircle, User, Mail, Phone, ChevronDown, ChevronUp } from 'lucide-react';

function OrgNodo({ nodo, nivel = 0, onEdit, onDelete, isAdmin }) {
  const [expanded, setExpanded] = useState(true);
  const hasHijos = nodo.hijos && nodo.hijos.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Línea superior (excepto raíz) */}
      {nivel > 0 && <div style={{ width: 2, height: 24, background: 'var(--color-gray-3)' }} />}
      
      <div style={{ 
        background: 'white', border: `2px solid ${nodo.color || 'var(--color-gray-3)'}`,
        borderRadius: 'var(--radius)', padding: '16px 20px',
        minWidth: 200, maxWidth: 260, textAlign: 'center',
        boxShadow: 'var(--shadow)', position: 'relative',
        transition: 'var(--transition)'
      }}>
        {/* Avatar */}
        <div style={{ 
          width: 44, height: 44, borderRadius: '50%', 
          background: nodo.color || 'var(--color-gray-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 10px', color: 'white', fontWeight: 700, fontSize: '1rem'
        }}>
          {`${nodo.nombre?.[0] || ''}${nodo.apellidos?.[0] || ''}`.toUpperCase() || <User size={18} />}
        </div>
        
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--color-text)' }}>
          {nodo.nombre} {nodo.apellidos}
        </div>
        <div style={{ 
          fontSize: '0.75rem', fontWeight: 600, color: nodo.color || 'var(--color-primary)',
          marginTop: 4, lineHeight: 1.3
        }}>
          {nodo.cargo}
        </div>
        {nodo.departamento && (
          <div style={{
            display: 'inline-block', fontSize: '0.68rem', fontWeight: 600,
            padding: '2px 8px', borderRadius: 20, marginTop: 6,
            background: `${nodo.color}20` || 'var(--color-gray-2)',
            color: nodo.color || 'var(--color-text-3)'
          }}>
            {nodo.departamento}
          </div>
        )}
        {(nodo.email || nodo.telefono) && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nodo.email && <div style={{ fontSize: '0.72rem', color: 'var(--color-text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><Mail size={10} />{nodo.email}</div>}
            {nodo.telefono && <div style={{ fontSize: '0.72rem', color: 'var(--color-text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><Phone size={10} />{nodo.telefono}</div>}
          </div>
        )}
        {nodo.responsabilidades && (
          <div style={{ 
            marginTop: 10, fontSize: '0.72rem', color: 'var(--color-text-3)',
            background: 'var(--color-gray-1)', borderRadius: 6, padding: '6px 8px',
            textAlign: 'left', lineHeight: 1.5
          }}>
            {nodo.responsabilidades.split(',').map((r, i) => (
              <div key={i}>· {r.trim()}</div>
            ))}
          </div>
        )}
        
        {/* Acciones admin */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 10 }}>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEdit(nodo)} title="Editar">
              <Edit size={13} />
            </button>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onDelete(nodo.id)} title="Eliminar" style={{ color: 'var(--color-danger)' }}>
              <Trash2 size={13} />
            </button>
          </div>
        )}

        {/* Toggle expansión */}
        {hasHijos && (
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ position: 'absolute', bottom: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--color-gray-3)', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 1 }}
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
      </div>

      {/* Hijos */}
      {hasHijos && expanded && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 2, height: 12, background: 'var(--color-gray-3)' }} />
          {nodo.hijos.length === 1 ? (
            <OrgNodo nodo={nodo.hijos[0]} nivel={nivel + 1} onEdit={onEdit} onDelete={onDelete} isAdmin={isAdmin} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 30, position: 'relative' }}>
              {/* Línea horizontal */}
              <div style={{ 
                position: 'absolute', top: 0, left: '10%', right: '10%',
                height: 2, background: 'var(--color-gray-3)'
              }} />
              {nodo.hijos.map((hijo, i) => (
                <OrgNodo key={hijo.id} nodo={hijo} nivel={nivel + 1} onEdit={onEdit} onDelete={onDelete} isAdmin={isAdmin} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ModalNodo({ nodo, nodos, onClose, onGuardado }) {
  const [form, setForm] = useState(nodo || {
    nombre: '', apellidos: '', cargo: '', departamento: '', email: '',
    telefono: '', responsabilidades: '', padre_id: '', orden: 0, color: '#e91e8c'
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const allNodos = [];
  const flattenNodos = (arr) => arr.forEach(n => { allNodos.push(n); if (n.hijos) flattenNodos(n.hijos); });
  flattenNodos(nodos);

  const handleGuardar = async () => {
    setLoading(true);
    try {
      const data = { ...form, padre_id: form.padre_id || null };
      if (form.id) await api.put(`/organigrama/${form.id}`, data);
      else await api.post('/organigrama', data);
      onGuardado();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{form.id ? 'Editar' : 'Nuevo'} Miembro del Organigrama</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Nombre <span className="required">*</span></label><input className="form-control" value={form.nombre} onChange={e => set('nombre', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Apellidos</label><input className="form-control" value={form.apellidos} onChange={e => set('apellidos', e.target.value)} /></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Cargo <span className="required">*</span></label><input className="form-control" value={form.cargo} onChange={e => set('cargo', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Departamento</label><input className="form-control" value={form.departamento} onChange={e => set('departamento', e.target.value)} /></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-control" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Teléfono</label><input className="form-control" value={form.telefono} onChange={e => set('telefono', e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label className="form-label">Responsabilidades (separadas por comas)</label>
            <textarea className="form-control" rows={3} value={form.responsabilidades} onChange={e => set('responsabilidades', e.target.value)} placeholder="Elaboración de PNTs, Firma de documentos, Control de calidad..." />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Depende de</label>
              <select className="form-control" value={form.padre_id || ''} onChange={e => set('padre_id', e.target.value)}>
                <option value="">— Nivel raíz —</option>
                {allNodos.filter(n => n.id !== form.id).map(n => (
                  <option key={n.id} value={n.id}>{n.nombre} {n.apellidos} — {n.cargo}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Color del nodo</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.color || '#e91e8c'} onChange={e => set('color', e.target.value)} style={{ width: 42, height: 38, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                {['#e91e8c','#1a1a1a','#9c27b0','#2196f3','#4caf50','#ff9800','#f44336','#009688'].map(c => (
                  <div key={c} onClick={() => set('color', c)} style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', border: form.color === c ? '3px solid white' : '2px solid transparent', boxShadow: '0 0 0 2px #eee' }} />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={loading}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : null}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrganigramaPage() {
  const { isAdmin } = useAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/organigrama').then(r => setTree(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este miembro del organigrama?')) return;
    await api.delete(`/organigrama/${id}`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GitBranch size={24} color="var(--color-primary)" /> Organigrama de LOMHIFAR S.L.
          </h1>
          <p>Estructura organizativa y responsabilidades</p>
        </div>
        {isAdmin() && (
          <button className="btn btn-primary" onClick={() => setModal({})}>
            <Plus size={16} /> Añadir Miembro
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 40, overflowX: 'auto' }}>
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          tree.length === 0 ? (
            <div className="empty-state"><GitBranch size={48} /><h3>Organigrama vacío</h3><p>Añade miembros para construir el organigrama</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'fit-content' }}>
              {tree.map(nodo => (
                <OrgNodo key={nodo.id} nodo={nodo} onEdit={n => setModal(n)} onDelete={handleDelete} isAdmin={isAdmin()} />
              ))}
            </div>
          )
        }
      </div>

      {modal !== null && (
        <ModalNodo
          nodo={modal.id ? modal : null}
          nodos={tree}
          onClose={() => setModal(null)}
          onGuardado={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
